import { useState, useEffect, useCallback } from "react";
import { getAPInventory, getAPInventoryWithImages, generateViewfinderLink, getViewfinderToken, getAPAttributes, uploadAPAttributes, downloadAPAttributes, uploadAPProductImage, getAPProductImage } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Eye, Link2, Copy, Package, Image, ClipboardList, Search, Upload, Download, Camera } from "lucide-react";
import { toast } from "sonner";

function LazyImage({ modelName, hasImage, onClick }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!hasImage) return;
    setLoading(true);
    getAPProductImage(modelName)
      .then(r => setSrc(r.data?.image_url || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [modelName, hasImage]);

  return (
    <div className="aspect-square bg-slate-100 flex items-center justify-center relative group cursor-pointer" onClick={onClick}>
      {loading ? (
        <div className="animate-pulse w-full h-full bg-slate-200" />
      ) : src ? (
        <img src={src} alt={modelName} className="w-full h-full object-cover" />
      ) : (
        <div className="text-center"><Image className="w-12 h-12 text-slate-300 mx-auto" /><p className="text-xs text-slate-400 mt-1">No image</p></div>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
        <div className="bg-white rounded-full p-2.5 shadow-lg"><Camera className="w-5 h-5 text-slate-700" /></div>
      </div>
    </div>
  );
}

export default function Viewfinder() {
  const [items, setItems] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareToken, setShareToken] = useState("");
  const [view, setView] = useState("inventory");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    Promise.all([
      getAPInventoryWithImages().catch(() => ({ data: [] })),
      getViewfinderToken().catch(() => ({ data: { token: "" } })),
      getAPAttributes().catch(() => ({ data: [] })),
    ])
      .then(([inv, tok, attr]) => { setItems(inv.data || []); setShareToken(tok.data?.token || ""); setAttributes(attr.data || []); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const shareUrl = shareToken ? `${window.location.origin}/viewfinder/${shareToken}` : "";
  const filtered = items.filter(i => i.model_name?.toLowerCase().includes(search.toLowerCase()));

  // Merge inventory with attributes for the attributes view
  const attrMap = {};
  attributes.forEach(a => { attrMap[a.product?.toUpperCase()] = a; });
  const mergedAttrs = filtered.map(item => {
    const a = attrMap[item.model_name?.toUpperCase()] || {};
    return { ...item, ...a, product: item.model_name };
  });

  const handleGenerateLink = async () => {
    try { const r = await generateViewfinderLink(); setShareToken(r.data.token); toast.success("Share link generated"); } catch { toast.error("Failed"); }
  };

  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };

  const handleUploadAttrs = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const r = await uploadAPAttributes(file); toast.success(r.data.message); const a = await getAPAttributes(); setAttributes(a.data || []); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const handleDownloadAttrs = async () => {
    try {
      const r = await downloadAPAttributes();
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "ap_product_attributes.xlsx"; a.click();
    } catch { toast.error("Download failed"); }
  };

  const handleImageUpload = (modelName) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        await uploadAPProductImage(modelName, file);
        toast.success("Image updated");
        const r = await getAPInventoryWithImages().catch(() => ({ data: [] }));
        setItems(r.data || []);
      } catch { toast.error("Image upload failed"); }
    };
    input.click();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="viewfinder-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Eye className="w-5 h-5" /> AP Viewfinder</h1>
          <p className="text-sm text-slate-500 mt-0.5">Third-party inventory view — {items.length} products</p>
        </div>
        <div className="flex items-center gap-2">
          {shareToken ? (
            <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-1.5">
              <Link2 className="w-4 h-4 text-slate-500" />
              <span className="text-xs text-slate-600 font-mono max-w-[200px] truncate">{shareUrl}</span>
              <Button variant="ghost" size="sm" className="h-6 px-2" onClick={copyLink}><Copy className="w-3.5 h-3.5" /></Button>
            </div>
          ) : (
            <Button size="sm" onClick={handleGenerateLink} data-testid="generate-viewfinder-link"><Link2 className="w-4 h-4 mr-1" />Generate Share Link</Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {[{ id: "inventory", label: "Inventory", icon: Package }, { id: "attributes", label: "Attributes", icon: ClipboardList }].map(t => (
          <button key={t.id} onClick={() => setView(t.id)} data-testid={`viewfinder-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${view === t.id ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        {view === "attributes" && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleDownloadAttrs} data-testid="download-attrs-btn"><Download className="w-4 h-4 mr-1" />Download</Button>
            <Button size="sm" onClick={() => document.getElementById("attr-upload").click()} disabled={uploading} data-testid="upload-attrs-btn">
              <Upload className="w-4 h-4 mr-1" />{uploading ? "Uploading..." : "Upload"}
            </Button>
            <input id="attr-upload" type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUploadAttrs} />
          </div>
        )}
      </div>

      {view === "inventory" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(item => (
            <Card key={item.sr_no} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`vf-item-${item.sr_no}`}>
              <LazyImage modelName={item.model_name} hasImage={item.has_image} onClick={() => handleImageUpload(item.model_name)} />
              <CardContent className="p-3">
                <h3 className="font-medium text-sm text-slate-800 truncate">{item.model_name}</h3>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-lg font-bold text-slate-900">Rs.{item.mrp?.toLocaleString()}</span>
                  <Badge className={item.quantity > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                    {item.quantity > 0 ? `${item.quantity} in stock` : "Out of stock"}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {view === "attributes" && (
        <Card className="overflow-hidden">
          <ScrollArea className="max-h-[600px]">
            <table className="w-full text-sm" data-testid="vf-attributes-table">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase sticky top-0">
                <tr>
                  <th className="px-3 py-2.5 text-left">Product</th>
                  <th className="px-3 py-2.5 text-right">Stock</th>
                  <th className="px-3 py-2.5 text-right">MRP</th>
                  <th className="px-3 py-2.5 text-left">Case Size</th>
                  <th className="px-3 py-2.5 text-left">Case Material</th>
                  <th className="px-3 py-2.5 text-left">Strap Material</th>
                  <th className="px-3 py-2.5 text-left">Deployment</th>
                  <th className="px-3 py-2.5 text-left">Crystal</th>
                  <th className="px-3 py-2.5 text-left">Water Resist.</th>
                  <th className="px-3 py-2.5 text-left">Movement</th>
                  <th className="px-3 py-2.5 text-left">Power Reserve</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {mergedAttrs.map((item, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2.5 font-medium text-slate-800">{item.product}</td>
                    <td className="px-3 py-2.5 text-right"><span className={item.quantity === 0 ? "text-red-600 font-bold" : "font-medium"}>{item.quantity}</span></td>
                    <td className="px-3 py-2.5 text-right font-mono">Rs.{item.mrp?.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.case_size || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.case_material || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.strap_material || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.deployment_type || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.crystal || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.water_resistance || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.movement || "-"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{item.power_reserve || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </Card>
      )}
    </div>
  );
}
